import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'super_admin' | 'admin' | 'agent' | 'member';
  status: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize authentication on load - SIMPLIFIED
  useEffect(() => {
    console.log('AuthContext initializing...');

    // Simple check - if we have a token, assume we're logged in
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('Found saved user:', userData.email);
        setUser(userData);
        console.log('User restored successfully');
      } catch (error) {
        console.error('Error restoring user:', error);
        // Don't clear data - let them stay logged in
      }
    } else {
      console.log('No saved user found');
    }
    
    setLoading(false);
    console.log('AuthContext initialization complete');
  }, []);


  const login = async (email: string, password: string) => {
    try {
      setError(null);
      console.log('=== PRODUCTION LOGIN ===');
      console.log('Attempting login for:', email);
      
      // Make real API call to login endpoint
      const response = await api.post('/auth/login', {
        email,
        password
      });
      
      const { token, user: userData } = response.data;
      
      console.log('Login successful, received token');
      console.log('User data:', userData);
      
      // Store the token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));

      console.log('Token stored, setting user state');
      setUser(userData);
      console.log('User state updated, login complete');
      
      return Promise.resolve();
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setError(null);
    window.location.href = '/admin/login';
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};